import type { Device } from 'src/types/Device';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { CameraRecordingEncryption, CameraStatus } from '../types/camera-status';
import { devices_status } from 'src/stores';
import { getCameraComponentKey } from './camera-helpers';
import { getCameraConfig } from './camera-settings-helpers';

/**
 * Whether the device reports the given key inside its `camera:{n}` config.
 *
 * @param path - Dot-separated path relative to the camera component, e.g.
 * `'rtsp'` or `'video.antiflicker'`.
 */
export const hasCameraConfigKey = (device: Device, path: string): boolean => {
	const segments = path.split('.');
	const key = segments.pop() ?? '';
	const parent = segments.reduce<any>((node, segment) => node?.[segment], getCameraConfig(device));

	return Object.hasOwn(parent ?? {}, key);
};

/**
 * @param deviceStatus - This device's status; looked up by real id when omitted.
 * Svelte callers pass `$devices_status[realId]` to stay reactive.
 */
const getRecordingEncryptionStatus = (
	device: Device,
	deviceStatus: DeviceStatusG2 = devices_status.getByRealId(device?.id),
): CameraRecordingEncryption | undefined => {
	const cameraKey = getCameraComponentKey(device);
	const cameraStatus = deviceStatus?.[cameraKey] as CameraStatus | undefined;
	return cameraStatus?.recording_encryption;
};

/**
 * Whether cloud recording encryption is available for this device on this account:
 * the firmware can create and rotate the recording master key, and the camera is
 * not shared with us — a guest never owns that key, so setting one up, unlocking
 * recordings and rotating are all off limits.
 */
export const isCameraEncryptionSupported = (device: Device, deviceStatus?: DeviceStatusG2): boolean =>
	!device?.shared && !!getRecordingEncryptionStatus(device, deviceStatus);

/** Whether the camera itself currently holds a recording master key. */
export const isCameraEncryptionConfigured = (device: Device, deviceStatus?: DeviceStatusG2): boolean =>
	getRecordingEncryptionStatus(device, deviceStatus)?.configured === true;

/**
 * Whether the firmware serves the `Camera.Set` RPC. The firmware that moved `arm` and
 * `privacy` out of the config and into the status is the same one that introduced
 * `Camera.Set`, so their presence in status is the probe.
 *
 * @deprecated LEGACY_CAMERA_ARM_PRIVACY - once no supported firmware keeps these in
 * config there is nothing left to probe.
 *
 * @param deviceStatus - This device's status; looked up by real id when omitted.
 * Svelte callers pass `$devices_status[realId]` to stay reactive.
 */
export const supportsCameraSetRpc = (
	device: Device,
	deviceStatus: DeviceStatusG2 = devices_status.getByRealId(device?.id),
): boolean => {
	const cameraStatus = deviceStatus?.[getCameraComponentKey(device)] as CameraStatus | undefined;
	return !!cameraStatus && ('arm' in cameraStatus || 'privacy' in cameraStatus);
};

/**
 * Build the RPC call that sets `arm` and/or `privacy`, picking the shape this firmware
 * understands. Both fields may be sent in one call.
 */
export const buildCameraArmPrivacyCall = (
	device: Device,
	patch: { arm?: boolean; privacy?: boolean },
	deviceStatus?: DeviceStatusG2,
): { method: string; params: Record<string, unknown> } => {
	const id = device?.channel || 0;
	return supportsCameraSetRpc(device, deviceStatus)
		? { method: 'Camera.Set', params: { id, ...patch } }
		: // LEGACY_CAMERA_ARM_PRIVACY: remove this branch, and the probe, with the old firmware.
			{ method: 'Camera.SetConfig', params: { id, config: patch } };
};
